import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Plus, Edit01, Trash01, SearchLg, Eye, XCircle, Tag01 } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { useTranslation } from 'react-i18next';
import { useKnowledgeBases, useDeleteKnowledgeBase, useUpdateKnowledgeBase, useAllKnowledgeDocuments, useDeleteKnowledgeDocument } from '@/hooks/api/use-knowledge-bases';
import { useKnowledgeTags, useAutoTagDocument } from '@/hooks/api/use-knowledge-tags';
import type { KnowledgeBaseListFilters, KnowledgeBaseResponse } from '@/types/api/knowledge-bases';
import type { KnowledgeBaseType } from '@/types/knowledge-base';
import type { BadgeColors } from '@/components/base/badges/badge-types';
import { useToast } from '@/hooks/use-toast';
import { DocumentDetailDrawer } from './[knowledgeBaseId]/document-detail-drawer';

const SOURCE_TYPE_CONFIG: Record<string, { label: string; color: BadgeColors }> = {
    upload: { label: 'Upload', color: 'gray' },
    seed_data: { label: 'Seed Data', color: 'gray' },
    training_email: { label: 'Training Email', color: 'blue' },
    review_resolution: { label: 'Review Center', color: 'success' },
    conversation: { label: 'Agent Chat', color: 'warning' },
    episode: { label: 'Episode', color: 'brand' },
    agent_analysis: { label: 'Agent Analysis', color: 'brand' },
};

export const KnowledgeBaseListPage = () => {
    const { t } = useTranslation(['pages:knowledge-admin', 'common']);
    const navigate = useNavigate();
    const toast = useToast();

    // KB list state
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState<KnowledgeBaseType | ''>('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [kbToDelete, setKbToDelete] = useState<string | null>(null);
    const [editingKb, setEditingKb] = useState<KnowledgeBaseResponse | null>(null);
    const [editForm, setEditForm] = useState({ name: '', description: '', kb_type: '' as KnowledgeBaseType | '' });

    // Documents state
    const [docSearch, setDocSearch] = useState('');
    const [filterKb, setFilterKb] = useState<string>('');
    const [filterSource, setFilterSource] = useState<string>('');
    const [filterPriority, setFilterPriority] = useState<string>('');
    const [filterAccess, setFilterAccess] = useState<string>('');
    const [filterTags, setFilterTags] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
    const [selectedDocKbId, setSelectedDocKbId] = useState<string | null>(null);
    const [deleteDocId, setDeleteDocId] = useState<string | null>(null);
    const DOCS_PER_PAGE = 20;
    const [sortField] = useState<'tags' | 'title' | ''>('');
    const [sortDir] = useState<'asc' | 'desc'>('desc');

    // API hooks
    const filters: KnowledgeBaseListFilters = useMemo(() => ({
        search: searchTerm || undefined,
        kb_type: typeFilter || undefined,
        limit: 500,
    }), [searchTerm, typeFilter]);

    const { data: knowledgeBases = [], isLoading } = useKnowledgeBases(filters);
    const { data: allDocuments = [] } = useAllKnowledgeDocuments();
    const { data: tagGroups = [] } = useKnowledgeTags();
    const deleteKnowledgeBase = useDeleteKnowledgeBase();
    const updateKnowledgeBase = useUpdateKnowledgeBase();
    const deleteDocument = useDeleteKnowledgeDocument();
    const autoTag = useAutoTagDocument();
    const [batchTagging, setBatchTagging] = useState(false);
    const [batchTagProgress, setBatchTagProgress] = useState({ done: 0, total: 0 });

    const typeOptions = [
        { id: '', label: t('typePlaceholder') },
        { id: 'general', label: t('types.general') },
        { id: 'documents', label: t('types.documents') },
        { id: 'routes', label: t('types.routes') },
        { id: 'regulation', label: t('types.regulation') },
        { id: 'company/account-specific', label: t('types.companyAccountSpecific') },
    ];

    // KB list options for document filter
    const kbOptions = useMemo(() => [
        { id: '', label: 'All Knowledge Bases' },
        ...knowledgeBases.map(kb => ({ id: kb.knowledge_base_id, label: `${kb.name} (${kb.document_count})` }))
    ], [knowledgeBases]);

    // Source types from documents
    const allSourceTypes = useMemo(() => {
        const types = new Set<string>();
        allDocuments.forEach(d => { if (d.source_type) types.add(d.source_type); });
        return Array.from(types).sort();
    }, [allDocuments]);

    const allAccessLevels = useMemo(() => {
        const levels = new Set<string>();
        allDocuments.forEach(d => levels.add(d.access_level));
        return Array.from(levels).sort();
    }, [allDocuments]);

    // Filtered documents (AND logic)
    const filteredDocuments = useMemo(() => {
        let result = allDocuments;
        if (docSearch) {
            const q = docSearch.toLowerCase();
            result = result.filter(d => d.title.toLowerCase().includes(q));
        }
        if (filterKb) result = result.filter(d => d.knowledge_base_id === filterKb);
        if (filterSource) result = result.filter(d => d.source_type === filterSource);
        if (filterPriority) result = result.filter(d => d.priority === parseInt(filterPriority));
        if (filterAccess) result = result.filter(d => d.access_level === filterAccess);
        if (filterTags.length > 0) {
            result = result.filter(d => filterTags.every(slug => d.canonical_tags?.includes(slug)));
        }
        // Sort
        if (sortField === 'tags') {
            const mul = sortDir === 'asc' ? 1 : -1;
            result = [...result].sort((a, b) => ((a.canonical_tags?.length || 0) - (b.canonical_tags?.length || 0)) * mul);
        } else if (sortField === 'title') {
            const mul = sortDir === 'asc' ? 1 : -1;
            result = [...result].sort((a, b) => a.title.localeCompare(b.title) * mul);
        }
        return result;
    }, [allDocuments, docSearch, filterKb, filterSource, filterPriority, filterAccess, filterTags, sortField, sortDir]);

    useEffect(() => { setCurrentPage(1); }, [docSearch, filterKb, filterSource, filterPriority, filterAccess, filterTags]);

    const toBadgeColor = useCallback((color: string): BadgeColors => {
        const map: Record<string, BadgeColors> = {
            error: "error", warning: "warning", success: "success", brand: "brand",
            blue: "blue", gray: "gray",
        };
        return map[color] || "gray";
    }, []);

    // KB handlers
    const handleDeleteClick = (id: string) => { setKbToDelete(id); setIsDeleteModalOpen(true); };
    const handleDeleteConfirm = async () => {
        if (!kbToDelete) return;
        try {
            await deleteKnowledgeBase.mutateAsync(kbToDelete);
            toast.success(t('messages.deleteSuccess', { defaultValue: 'Knowledge base eliminata' }));
            setIsDeleteModalOpen(false); setKbToDelete(null);
        } catch { toast.error(t('messages.deleteError', { defaultValue: 'Errore eliminazione' })); }
    };
    const handleEditClick = (kb: KnowledgeBaseResponse) => {
        setEditingKb(kb);
        setEditForm({ name: kb.name, description: kb.description || '', kb_type: kb.kb_type });
    };
    const handleEditSave = async () => {
        if (!editingKb || !editForm.name.trim() || !editForm.kb_type) return;
        try {
            await updateKnowledgeBase.mutateAsync({ knowledgeBaseId: editingKb.knowledge_base_id, data: { name: editForm.name.trim(), description: editForm.description.trim() || null, kb_type: editForm.kb_type as KnowledgeBaseType } });
            toast.success(t('form.updateSuccess', { defaultValue: 'Knowledge base updated' })); setEditingKb(null);
        } catch { toast.error(t('form.updateError', { defaultValue: 'Error updating' })); }
    };

    // Document handlers
    const handleBatchAutoTag = async () => {
        const docsToTag = filteredDocuments.filter(d => !d.canonical_tags || d.canonical_tags.length <= 1);
        if (docsToTag.length === 0) { toast.success('All documents already tagged'); return; }
        setBatchTagging(true);
        setBatchTagProgress({ done: 0, total: docsToTag.length });
        let done = 0;
        for (const doc of docsToTag) {
            try { await autoTag.mutateAsync(doc.doc_id); } catch { /* continue */ }
            done++;
            setBatchTagProgress({ done, total: docsToTag.length });
        }
        setBatchTagging(false);
        toast.success(`Auto-tagged ${done} documents`);
    };

    const handleDeleteDocument = async (docId: string, kbId: string) => {
        try {
            await deleteDocument.mutateAsync({ knowledgeBaseId: kbId, documentId: docId });
            toast.success('Document deleted'); setDeleteDocId(null);
        } catch { toast.error('Error deleting document'); }
    };

    // Count tags on filtered documents (updates when filters change)
    const docTagCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        // Use filteredDocuments but WITHOUT the tag filter applied, so tag counts stay useful
        let base = allDocuments;
        if (docSearch) {
            const q = docSearch.toLowerCase();
            base = base.filter(d => d.title.toLowerCase().includes(q));
        }
        if (filterKb) base = base.filter(d => d.knowledge_base_id === filterKb);
        if (filterSource) base = base.filter(d => d.source_type === filterSource);
        if (filterPriority) base = base.filter(d => d.priority === parseInt(filterPriority));
        if (filterAccess) base = base.filter(d => d.access_level === filterAccess);
        base.forEach(d => {
            d.canonical_tags?.forEach(slug => {
                counts[slug] = (counts[slug] || 0) + 1;
            });
        });
        return counts;
    }, [allDocuments, docSearch, filterKb, filterSource, filterPriority, filterAccess]);

    const getTypeBadge = (type: KnowledgeBaseType) => {
        const typeLabels: Record<KnowledgeBaseType, string> = {
            general: t('types.general'), documents: t('types.documents'), routes: t('types.routes'),
            regulation: t('types.regulation'), 'company/account-specific': t('types.companyAccountSpecific'),
        };
        return <Badge color="gray" size="sm">{typeLabels[type]}</Badge>;
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4" />
                <p className="text-tertiary">{t('loading')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">{t('title')}</h2>
                    <p className="text-tertiary mt-1">{t('subtitle')}</p>
                </div>
                <Button onClick={() => navigate('/admin/knowledge/new')} size="md" color="primary" iconLeading={Plus}>
                    {t('newKnowledgeBase')}
                </Button>
            </div>

            {/* KB Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input placeholder={t('searchPlaceholder')} value={searchTerm} onChange={setSearchTerm} icon={SearchLg} size="md" />
                    <Select placeholder={t('typePlaceholder')} items={typeOptions} selectedKey={typeFilter} onSelectionChange={(key) => setTypeFilter(key as KnowledgeBaseType | '')} size="md">
                        {(item) => <Select.Item key={item.id} id={item.id}>{item.label}</Select.Item>}
                    </Select>
                </div>
            </div>

            {/* Knowledge Bases Table */}
            <TableCard.Root>
                <TableCard.Header title={`${t('title')} (${knowledgeBases.length})`} description={t('subtitle')} />
                <Table>
                    <Table.Header>
                        <Table.Head label={t('headers.name')} isRowHeader />
                        <Table.Head label={t('headers.description')} />
                        <Table.Head label={t('headers.type')} />
                        <Table.Head label={t('headers.documentCount')} />
                        <Table.Head label={t('headers.createdAt')} />
                        <Table.Head label={t('headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {knowledgeBases.map((kb) => (
                            <Table.Row key={kb.knowledge_base_id}>
                                <Table.Cell>
                                    <button onClick={() => setFilterKb(kb.knowledge_base_id)} className="font-medium text-brand hover:underline text-left">
                                        {kb.name}
                                    </button>
                                </Table.Cell>
                                <Table.Cell><span className="text-sm text-tertiary">{kb.description || '-'}</span></Table.Cell>
                                <Table.Cell>{getTypeBadge(kb.kb_type)}</Table.Cell>
                                <Table.Cell><span className="text-sm text-primary">{kb.document_count}</span></Table.Cell>
                                <Table.Cell><span className="text-sm text-tertiary">{new Date(kb.created_at).toLocaleDateString()}</span></Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Button onClick={() => handleEditClick(kb)} size="sm" color="secondary" iconLeading={Edit01}>{t('actions.edit')}</Button>
                                        <Button onClick={() => handleDeleteClick(kb.knowledge_base_id)} size="sm" color="secondary-destructive" iconLeading={Trash01}>{t('actions.delete')}</Button>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {/* ==================== Documents Section (cross-KB) ==================== */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-primary">Documents ({allDocuments.length})</h3>
                    <div className="flex items-center gap-2">
                        <Button onClick={handleBatchAutoTag} size="sm" color="secondary" iconLeading={Tag01} isDisabled={batchTagging}>
                            {batchTagging ? `Tagging ${batchTagProgress.done}/${batchTagProgress.total}...` : 'Auto-tag All'}
                        </Button>
                        <Button onClick={() => navigate('/admin/knowledge/new')} size="sm" color="primary" iconLeading={Plus}>
                            Upload Document
                        </Button>
                    </div>
                </div>

                {/* Document Filters */}
                <div className="space-y-3 mb-4">
                    <Input placeholder="Search documents..." value={docSearch} onChange={setDocSearch} icon={SearchLg} size="md" />
                    <div className="flex flex-wrap gap-2">
                        <div className="min-w-[240px]">
                            <Select placeholder="Knowledge Base" items={kbOptions} selectedKey={filterKb} onSelectionChange={(key) => setFilterKb(key as string)} size="sm">
                                {(item) => <Select.Item key={item.id} id={item.id}>{item.label}</Select.Item>}
                            </Select>
                        </div>
                        <Select placeholder="Source" items={[{ id: '', label: 'All Sources' }, ...allSourceTypes.map(s => ({ id: s, label: SOURCE_TYPE_CONFIG[s]?.label || s }))]} selectedKey={filterSource} onSelectionChange={(key) => setFilterSource(key as string)} size="sm">
                            {(item) => <Select.Item key={item.id} id={item.id}>{item.label}</Select.Item>}
                        </Select>
                        <Select placeholder="Priority" items={[{ id: '', label: 'All Priorities' }, { id: '1', label: '1' }, { id: '2', label: '2' }, { id: '3', label: '3' }, { id: '4', label: '4' }, { id: '5', label: '5' }]} selectedKey={filterPriority} onSelectionChange={(key) => setFilterPriority(key as string)} size="sm">
                            {(item) => <Select.Item key={item.id} id={item.id}>{item.label}</Select.Item>}
                        </Select>
                        <Select placeholder="Access Level" items={[{ id: '', label: 'All Levels' }, ...allAccessLevels.map(a => ({ id: a, label: a }))]} selectedKey={filterAccess} onSelectionChange={(key) => setFilterAccess(key as string)} size="sm">
                            {(item) => <Select.Item key={item.id} id={item.id}>{item.label}</Select.Item>}
                        </Select>
                        {(filterKb || filterSource || filterPriority || filterAccess || filterTags.length > 0 || docSearch) && (
                            <Button onClick={() => { setDocSearch(''); setFilterKb(''); setFilterSource(''); setFilterPriority(''); setFilterAccess(''); setFilterTags([]); }} size="sm" color="secondary" iconLeading={XCircle}>
                                Clear
                            </Button>
                        )}
                    </div>

                    {/* Grouped tag filters */}
                    {tagGroups.length > 0 && (
                        <div className="space-y-2">
                            {tagGroups.map(group => {
                                if (!group.tags || group.tags.length === 0) return null;
                                // Only show tags with documents or active filters
                                const visibleTags = group.tags.filter(tag => (docTagCounts[tag.slug] || 0) > 0 || filterTags.includes(tag.slug));
                                if (visibleTags.length === 0) return null;
                                const groupColor = toBadgeColor(group.color);
                                return (
                                    <div key={group.group_id} className="flex items-start gap-3">
                                        <Badge color={groupColor} size="sm">{group.label}</Badge>
                                        <div className="flex flex-wrap gap-1.5">
                                            {visibleTags.map(tag => {
                                                const isActive = filterTags.includes(tag.slug);
                                                const count = docTagCounts[tag.slug] || 0;
                                                return (
                                                    <button key={tag.tag_id} type="button"
                                                        onClick={() => setFilterTags(prev => prev.includes(tag.slug) ? prev.filter(s => s !== tag.slug) : [...prev, tag.slug])}
                                                        className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition-colors ${
                                                            isActive ? 'bg-utility-brand-50 text-utility-brand-700 ring-utility-brand-200' : 'bg-utility-gray-50 text-utility-gray-600 ring-utility-gray-200 hover:bg-utility-gray-100'
                                                        }`}
                                                    >
                                                        {tag.label} {count > 0 && <span className="opacity-50">{count}</span>}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Selected tags bar */}
                    {filterTags.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap rounded-lg bg-secondary/50 px-3 py-2">
                            <span className="text-xs text-tertiary shrink-0">Active filters:</span>
                            {filterTags.map(slug => {
                                // Find tag label from tagGroups
                                let label = slug;
                                for (const g of tagGroups) {
                                    const found = g.tags?.find(t => t.slug === slug);
                                    if (found) { label = found.label; break; }
                                }
                                return (
                                    <button
                                        key={slug}
                                        onClick={() => setFilterTags(prev => prev.filter(s => s !== slug))}
                                        className="inline-flex items-center gap-1 rounded-md bg-utility-brand-50 text-utility-brand-700 ring-1 ring-inset ring-utility-brand-200 px-2 py-0.5 text-xs font-medium hover:bg-utility-brand-100 transition-colors"
                                    >
                                        {label}
                                        <XCircle className="size-3" />
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {filteredDocuments.length !== allDocuments.length && (
                        <p className="text-xs text-tertiary">{filteredDocuments.length} of {allDocuments.length} documents</p>
                    )}
                </div>

                {/* Documents Table */}
                {filteredDocuments.length === 0 ? (
                    <p className="text-tertiary text-center py-8">{allDocuments.length === 0 ? 'No documents' : 'No documents match filters'}</p>
                ) : (<>
                    <TableCard.Root>
                        <Table>
                            <Table.Header>
                                <Table.Head label="Title" isRowHeader />
                                <Table.Head label="KB" />
                                <Table.Head label="Source" />
                                <Table.Head label="Priority" />
                                <Table.Head label="Access" />
                                <Table.Head label={`Tags ${sortField === 'tags' ? (sortDir === 'asc' ? '↑' : '↓') : ''}`} />
                                <Table.Head label="Embedded" />
                                <Table.Head label="Chunks" />
                                <Table.Head label="Actions" />
                            </Table.Header>
                            <Table.Body>
                                {filteredDocuments.slice((currentPage - 1) * DOCS_PER_PAGE, currentPage * DOCS_PER_PAGE).map((doc) => {
                                    const sourceConfig = SOURCE_TYPE_CONFIG[doc.source_type || ''] || { label: doc.source_type || '?', color: 'gray' as BadgeColors };
                                    return (
                                        <Table.Row key={doc.doc_id}>
                                            <Table.Cell>
                                                <button onClick={() => { setSelectedDocId(doc.doc_id); setSelectedDocKbId(doc.knowledge_base_id); }} className="font-medium text-brand hover:underline text-left">
                                                    {doc.title}
                                                </button>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-xs text-tertiary">{doc.knowledge_base_name}</span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Badge color={sourceConfig.color} size="sm">{sourceConfig.label}</Badge>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Badge color="gray" size="sm">{doc.priority}</Badge>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Badge color={doc.access_level === 'public' ? 'success' : doc.access_level === 'company' ? 'blue' : 'warning'} size="sm">{doc.access_level}</Badge>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className={`text-sm ${(doc.canonical_tags?.length || 0) > 0 ? 'text-primary' : 'text-tertiary'}`}>
                                                    {doc.canonical_tags?.length || 0}
                                                </span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Badge color={doc.has_embedding ? 'success' : 'gray'} size="sm">{doc.has_embedding ? 'Yes' : 'No'}</Badge>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-primary">{doc.chunk_count}</span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-1">
                                                    <Button onClick={() => { setSelectedDocId(doc.doc_id); setSelectedDocKbId(doc.knowledge_base_id); }} size="sm" color="secondary" iconLeading={Eye}>
                                                        View
                                                    </Button>
                                                    {deleteDocId === doc.doc_id ? (
                                                        <div className="flex items-center gap-1">
                                                            <Button onClick={() => setDeleteDocId(null)} size="sm" color="secondary">Cancel</Button>
                                                            <Button onClick={() => handleDeleteDocument(doc.doc_id, doc.knowledge_base_id)} size="sm" color="primary-destructive" isDisabled={deleteDocument.isPending}>
                                                                {deleteDocument.isPending ? '...' : 'Delete'}
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <Button onClick={() => setDeleteDocId(doc.doc_id)} size="sm" color="secondary-destructive" iconLeading={Trash01}>
                                                            Remove
                                                        </Button>
                                                    )}
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table>
                    </TableCard.Root>
                    {filteredDocuments.length > DOCS_PER_PAGE && (
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-secondary">
                            <span className="text-sm text-tertiary">
                                {((currentPage - 1) * DOCS_PER_PAGE) + 1}–{Math.min(currentPage * DOCS_PER_PAGE, filteredDocuments.length)} of {filteredDocuments.length}
                            </span>
                            <div className="flex items-center gap-2">
                                <Button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} size="sm" color="secondary" isDisabled={currentPage === 1}>Previous</Button>
                                <span className="text-sm text-primary px-2">{currentPage} / {Math.ceil(filteredDocuments.length / DOCS_PER_PAGE)}</span>
                                <Button onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredDocuments.length / DOCS_PER_PAGE), p + 1))} size="sm" color="secondary" isDisabled={currentPage >= Math.ceil(filteredDocuments.length / DOCS_PER_PAGE)}>Next</Button>
                            </div>
                        </div>
                    )}
                </>)}
            </div>

            {/* Document Detail Drawer */}
            {selectedDocId && selectedDocKbId && (
                <DocumentDetailDrawer
                    knowledgeBaseId={selectedDocKbId}
                    documentId={selectedDocId}
                    isOpen={!!selectedDocId}
                    onClose={() => { setSelectedDocId(null); setSelectedDocKbId(null); }}
                />
            )}

            {/* Delete KB Modal */}
            <DialogTrigger isOpen={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
                <ModalOverlay><Modal><Dialog>
                    <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                        <h2 className="text-xl font-semibold text-primary mb-4">{t('modal.confirmDelete')}</h2>
                        <p className="text-tertiary mb-6">{t('modal.confirmDeleteMessage')}</p>
                        <div className="flex items-center justify-end gap-4">
                            <Button onClick={() => { setIsDeleteModalOpen(false); setKbToDelete(null); }} size="md" color="secondary" isDisabled={deleteKnowledgeBase.isPending}>{t('modal.cancel')}</Button>
                            <Button onClick={handleDeleteConfirm} size="md" color="primary-destructive" isDisabled={deleteKnowledgeBase.isPending}>{deleteKnowledgeBase.isPending ? t('loading') : t('modal.delete')}</Button>
                        </div>
                    </div>
                </Dialog></Modal></ModalOverlay>
            </DialogTrigger>

            {/* Edit KB Modal */}
            <DialogTrigger isOpen={!!editingKb} onOpenChange={(open) => !open && setEditingKb(null)}>
                <ModalOverlay><Modal><Dialog>
                    <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                        <h2 className="text-xl font-semibold text-primary mb-4">{t('form.edit')}</h2>
                        <div className="space-y-4 mb-6">
                            <Input label={t('form.fields.name')} placeholder={t('form.fields.namePlaceholder')} value={editForm.name} onChange={(value) => setEditForm({ ...editForm, name: value })} isRequired size="md" />
                            <Input label={t('form.fields.description')} placeholder={t('form.fields.descriptionPlaceholder')} value={editForm.description} onChange={(value) => setEditForm({ ...editForm, description: value })} size="md" />
                            <Select label={t('form.fields.type')} placeholder={t('form.fields.typePlaceholder')} items={typeOptions.filter(o => o.id !== '')} selectedKey={editForm.kb_type} onSelectionChange={(key) => setEditForm({ ...editForm, kb_type: key as KnowledgeBaseType })} isRequired size="md">
                                {(item) => <Select.Item key={item.id} id={item.id}>{item.label}</Select.Item>}
                            </Select>
                        </div>
                        <div className="flex items-center justify-end gap-4">
                            <Button onClick={() => setEditingKb(null)} size="md" color="secondary" isDisabled={updateKnowledgeBase.isPending}>{t('form.cancel')}</Button>
                            <Button onClick={handleEditSave} size="md" color="primary" isDisabled={!editForm.name.trim() || !editForm.kb_type || updateKnowledgeBase.isPending}>{updateKnowledgeBase.isPending ? t('loading') : t('form.save')}</Button>
                        </div>
                    </div>
                </Dialog></Modal></ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
